/**
 * Entry gate (TMMAPP-0025; the subscribe gate since TMMAPP-0116). Every authenticated route below
 * `MeProvider` except `/shared/:virtualAccountId` passes through here. A person the subscribe gate
 * blocks (never subscribed, or past grace) gets the empty shell and the blocking subscribe dialog on
 * whatever route they opened; everyone else gets the route, and `/` sends them to their first
 * screen. Settings and `/auth/*` stay reachable for every blocked person (Operator, 2026-10-08;
 * TMMAPP-0117 B13 past grace, TMMAPP-0118 C12 never subscribed), so they can always pay, update
 * their card, leave a share, sign out or delete their account. A locked member also reaches My
 * Financial Assistant, which is then Help & feedback's tickets only (TMMAPP-0142 M1): the same
 * routes the shell's locked state leaves open (`isLockedExemptRoute`).
 *
 * A subscriber with no consent record for the served terms version (an admin-created subscription,
 * TMMAPP-0119 §A4) gets the route with the blocking first-sign-in consent dialog over it; Settings
 * and `/auth/*` stay reachable here too. Until that subscriber's consent read settles, the page is held
 * (inert, with a progress bar) so nothing can be used before the dialog can show (TMMAPP-0123 item 13).
 */
import { Box, LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { SubscribeGateContext } from '@/features/subscribe/subscribeOfferContext';
import { FirstSignInConsentDialog } from '@/features/subscribe/FirstSignInConsentDialog';
import { GatedShell } from '@/shell/GatedShell';
import { useConsentGate } from './consentGate';
import { useMe } from './meHooks';
import { isLockedExemptRoute, isUnlockedRoute } from './LockedScreen';
import { consumeReturnTo } from './returnTo';
import { useSubscribeGate } from './subscribeGate';

export function EntryGate() {
  const { t } = useTranslation();
  const me = useMe();
  const location = useLocation();
  const gate = useSubscribeGate();
  // A subscriber's first-sign-in consent (TMMAPP-0119 §A4): blocking, except on Settings.
  const consent = useConsentGate(gate === 'open');

  // Settings renders unblocked for a blocked person (`/auth/*` is outside this gate); a locked
  // member's tickets too.
  const unblocked =
    gate === 'blocking' &&
    (isUnlockedRoute(location.pathname) || (me.locked && isLockedExemptRoute(location.pathname)));
  if ((gate === 'blocking' && !unblocked) || gate === 'checking') {
    return <GatedShell blocking={gate === 'blocking'} />;
  }

  // Consume returnTo on the first allowed render of `/`.
  if (location.pathname === '/') {
    const returnTo = consumeReturnTo();
    if (returnTo && returnTo !== '/') {
      return <Navigate to={returnTo} replace />;
    }
    // Everyone opens on Home (TMMAPP-0152 §A 1), a GUEST included: invite acceptance lands on `/`
    // too (returnUrl is `/auth/return`), and a guest's Home is their first shared space (TMMAPP-0159).
    // The guest tour starts when the guest opens that virtual account.
    return <Navigate to="/me/home" replace />;
  }

  const held = consent.kind === 'checking' && !isUnlockedRoute(location.pathname);
  return (
    <SubscribeGateContext.Provider value={gate}>
      {held && (
        <LinearProgress
          aria-label={t('common.loading')}
          data-consent-checking=""
          sx={{ position: 'fixed', top: 0, insetInline: 0, zIndex: (theme) => theme.zIndex.modal }}
        />
      )}
      {/* A block box, so `aria-busy` sits on an element in the accessibility tree (a
          `display: contents` box has none; TMMAPP-0124 item 19). */}
      <Box inert={held} aria-busy={held || undefined} data-entry-gate="">
        <Outlet />
      </Box>
      {consent.kind === 'required' && (
        <FirstSignInConsentDialog
          open={!isUnlockedRoute(location.pathname)}
          version={consent.version}
          missing={consent.missing}
          onAccepted={consent.accepted}
        />
      )}
    </SubscribeGateContext.Provider>
  );
}
